import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, UserRound, Building2 } from "lucide-react";

/**
 * QuickEnrol — a two-card enrolment shortcut sitting under the mission
 * statement. Each card deep-links into the nztaxation.nz-branded iframe
 * wrapper that surfaces the matching ClientOps enrolment form.
 */
export default function QuickEnrol() {
  return (
    <section
      id="enrol"
      className="relative bg-[#FAFAFA] border-b border-slate-200"
      data-testid="quick-enrol-section"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
            Quick enrol
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight">
            Pick your entity — <span className="text-[#C9A349]">we&apos;ll take it from here.</span>
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Under five minutes to sign up. You&apos;ll be greeted with a welcome call from a real
            NZ Taxation team member within 24 hours.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          <EnrolCard
            to="/enrol/sole-trader"
            icon={UserRound}
            kicker="Individual"
            title="Sole trader"
            body="Annual IR3 filed for you, GST returns while you're registered, free invoicing via Prosaic."
            price="NZ$75 + GST / month"
            testid="enrol-cta-sole-trader"
          />
          <EnrolCard
            to="/enrol/company"
            icon={Building2}
            kicker="Ltd company"
            title="2-owner company"
            body="Company tax return, GST, shareholder IR3s, Xero-first record keeping — the full package for a working partnership."
            price="See plan on next screen"
            featured
            testid="enrol-cta-company"
          />
        </div>

        <div className="mt-8 text-center text-xs text-slate-500">
          Not sure which entity is right for you?{" "}
          <Link to="/#pricing" className="font-heading font-bold text-[#0A192F] hover:text-[#C9A349]">
            Compare all plans
          </Link>
        </div>
      </div>
    </section>
  );
}

function EnrolCard({ to, icon: Icon, kicker, title, body, price, featured, testid }) {
  const base = featured
    ? "bg-[#0A192F] text-white border border-[#0A192F] hover:border-[#C9A349]"
    : "bg-white text-[#0F172A] border border-slate-200 hover:border-[#C9A349]";
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45 }}
      className={`${base} p-8 md:p-10 transition-colors flex flex-col`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-11 h-11 grid place-items-center ${
            featured ? "bg-[#C9A349] text-[#0A192F]" : "bg-[#0A192F] text-[#C9A349]"
          }`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
          {kicker}
        </div>
      </div>

      <h3 className="mt-6 font-heading font-black text-3xl md:text-4xl tracking-tight leading-tight">
        {title}
      </h3>
      <p
        className={`mt-3 leading-relaxed ${featured ? "text-slate-300" : "text-slate-600"}`}
      >
        {body}
      </p>

      <div
        className={`mt-6 text-sm font-heading font-bold uppercase tracking-[0.14em] ${
          featured ? "text-[#C9A349]" : "text-[#0A192F]"
        }`}
      >
        {price}
      </div>

      <Link
        to={to}
        data-testid={testid}
        className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 font-heading font-black uppercase tracking-[0.14em] text-sm transition-colors ${
          featured
            ? "bg-[#C9A349] text-[#0A192F] hover:bg-white"
            : "bg-[#0A192F] text-white hover:bg-[#C9A349] hover:text-[#0A192F]"
        }`}
      >
        Enrol now <ArrowRight className="w-4 h-4" />
      </Link>
    </motion.div>
  );
}
